import * as Yup from 'yup';
import FormSectionPage from '../form-section/formSectionPage';
import i18n from 'src/localization/i18n';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import {
    CREDIT_APPLICANT_EMAIL_ERROR,
    LABEL_FIELD_ERROR_REQUIRED,
} from 'src/localization/resouceKeys';

import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { FULL_NAME, USER_NAME } from 'src/config';
import React, { useEffect, useMemo, useState } from 'react';

interface IDealerInformation {
    dealerCode: string;
    requestorName: string;
    requestorEmail: string;
}

interface IProps {
    onSave: (values: IDealerInformation) => void;
    onFormTouched?: () => void;
}

const DEALER_CODE_REGEX = /^[A-Za-z0-9]{6}$/;
const REQUESTOR_EMAIL_REGEX =
    // eslint-disable-next-line max-len
    /^([a-zA-Z0-9_\u00C0-\u024F\-.]+)@([a-zA-Z0-9_\u00C0-\u024F\-.]+)\.([a-zA-Z\u00C0-\u024F]{2,63})$/i;
const MAX_EMAIL_LENGTH = 50;

const DealerInformation: React.FC<IProps> = ({ onSave, onFormTouched }) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const [showIcon, setShowIcon] = useState<boolean | null>(null);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [person, setPerson] = useState({ name: '', email: '' });
    const dealerId = useSelector(
        (state: IUser) => state.currentDealers?.default,
    );
    const permissionsToken = useSelector((state: IUser) => {
        return state.permissionsToken;
    });

    useEffect(() => {
        if (permissionsToken) {
            const fullname = sessionStorage.getItem(FULL_NAME);
            const username = sessionStorage.getItem(USER_NAME);
            if (fullname && username) {
                setPerson({
                    name: fullname,
                    email: username,
                });
            }
        }
    }, [permissionsToken]);

    const schema = useMemo(
        () =>
            Yup.object({
                dealerCode: Yup.string()
                    .matches(
                        DEALER_CODE_REGEX,
                        'Please enter a valid Dealer Code.',
                    )
                    .required('Field required'),
                requestorName: Yup.string().required(() =>
                    i18n.t(LABEL_FIELD_ERROR_REQUIRED),
                ),
                requestorEmail: Yup.string()
                    .transform((v) => v.trim().toLowerCase())
                    .required('Email address is required.')
                    .matches(REQUESTOR_EMAIL_REGEX, () =>
                        i18n.t(CREDIT_APPLICANT_EMAIL_ERROR),
                    )
                    .max(
                        MAX_EMAIL_LENGTH,
                        'Email address must not exceed 50 characters.',
                    ),
            }),
        [],
    );

    const initialValues = useMemo(
        () => ({
            dealerCode: dealerId ?? '',
            requestorName: person.name,
            requestorEmail: person.email,
        }),
        [dealerId, person.name, person.email],
    );

    const formik = useFormik<IDealerInformation>({
        initialValues,
        enableReinitialize: true,
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: (values) => {
            console.log('Dealer Information Data:', values);
            onSave(values);
            setShowIcon(true);
            setOpen(false);
            setIsDisabled(true);
        },
    });

    useEffect(() => {
        if (formik.dirty && !isDisabled) {
            onFormTouched?.();
        }
    }, [formik.dirty, isDisabled, onFormTouched]);

    const hasError = (field: keyof IDealerInformation) =>
        !!(formik.touched[field] && formik.errors[field]);

    const handleEdit = () => {
        setIsDisabled(false);
        setOpen(true);
        setShowIcon(null);
        formik.setTouched({});
        formik.setErrors({});
    };

    return (
        <FormSectionPage
            title={'Dealer Information'}
            handlerSubmit={formik.submitForm}
            isOpen={isOpen}
            setOpen={setOpen}
            showIcon={showIcon}
            isDisabled={isDisabled}
            onEdit={handleEdit}
            isFormValid={formik.isValid}
        >
            <div className='o-layout'>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='dealerCode'
                        labelName={'Dealer Code*'}
                        value={formik.values.dealerCode}
                        handlerChange={formik.handleChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('dealerCode')
                                ? formik.errors.dealerCode
                                : ''
                        }
                        asTouched={formik.touched.dealerCode}
                        disabled={true}
                    />
                </div>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='requestorName'
                        labelName={'Requestor Name*'}
                        value={formik.values.requestorName}
                        handlerChange={formik.handleChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('requestorName')
                                ? formik.errors.requestorName
                                : ''
                        }
                        asTouched={formik.touched.requestorName}
                        disabled={true}
                    />
                </div>
            </div>
            <Input
                type='email'
                name='requestorEmail'
                labelName={'Requestor Email*'}
                value={formik.values.requestorEmail}
                handlerChange={formik.handleChange}
                handlerBlur={formik.handleBlur}
                asErrors={
                    hasError('requestorEmail')
                        ? formik.errors.requestorEmail
                        : ''
                }
                asTouched={formik.touched.requestorEmail}
                disabled={isDisabled}
                maxlength={MAX_EMAIL_LENGTH}
            />
        </FormSectionPage>
    );
};

export default DealerInformation;
